import { isRetryableViewerLoadError } from "@/hooks/pdfViewer";
import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import { useCallback, useEffect, useRef, useState } from "react";

import { useSignedUrlWithRetry } from "./signedUrl";

const PANEL_WIDTH_CSS_VAR = "--panel-width";

interface UseCitationPanelParams {
  fileMapping: Record<string, string>;
  onFailure?: () => void;
}

export const useCitationPanel = ({ fileMapping, onFailure }: UseCitationPanelParams) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [fileName, setFileName] = useState("");
  const [fileId, setFileId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [width, setWidth] = useState(500);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const activeCitationFileIdRef = useRef<string | null>(null);

  const failCitation = useCallback(() => {
    onFailure?.();
    activeCitationFileIdRef.current = null;
    setIsOpen(false);
    setFileId(null);
  }, [onFailure]);

  const {
    url: fileUrl,
    onViewerLoadError,
    onViewerLoadSuccess,
  } = useSignedUrlWithRetry(fileId, {
    onUnrecoverableError: failCitation,
    isRetryableError: isRetryableViewerLoadError,
  });

  const clearPanelWidthStyle = useCallback(() => {
    document.documentElement.style.removeProperty(PANEL_WIDTH_CSS_VAR);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty(PANEL_WIDTH_CSS_VAR, `${width}px`);
      return clearPanelWidthStyle;
    }
    clearPanelWidthStyle();
    return undefined;
  }, [clearPanelWidthStyle, isOpen, width]);

  useEffect(() => () => clearPanelWidthStyle(), [clearPanelWidthStyle]);

  const openVisitCitation = useCallback(
    ({ file_id: citationFileId, start_page }: Pick<MedVisitCitation, "file_id" | "start_page">) => {
      if (!citationFileId) {
        console.error(`Invalid fileId provided: ${citationFileId}`);
        throw new Error("Invalid fileId");
      }

      const isNewFile = citationFileId !== activeCitationFileIdRef.current;
      activeCitationFileIdRef.current = citationFileId;
      const resolvedFileName = fileMapping[citationFileId] ?? "Unknown file";
      setIsOpen(true);
      setPageNumber(start_page);

      if (isNewFile) {
        setFileName(resolvedFileName);
        setFileId(String(citationFileId));
      }
    },
    [fileMapping]
  );

  const openCitation = useCallback(
    (citationFileId: string, pageNumbers: number[], _visitId?: string) => {
      openVisitCitation({ file_id: citationFileId, start_page: pageNumbers?.[0] ?? 1 });
    },
    [openVisitCitation]
  );

  const closeCitationPanel = useCallback(() => {
    activeCitationFileIdRef.current = null;
    setIsOpen(false);
    setFileId(null);
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const setCitationPanelWidth = useCallback((nextWidth: number) => {
    setWidth(nextWidth);
    document.documentElement.style.setProperty(PANEL_WIDTH_CSS_VAR, `${nextWidth}px`);
  }, []);

  const setCitationPage = useCallback((page: number) => {
    setPageNumber(page);
  }, []);

  return {
    citationPanel: {
      isOpen,
      fileId,
      fileName,
      fileUrl,
      pageNumber,
      width,
      onViewerLoadError,
      onViewerLoadSuccess,
    },
    openCitation,
    openVisitCitation,
    closeCitationPanel,
    setCitationPage,
    setCitationPanelWidth,
    isFullscreen,
    toggleFullscreen,
  };
};
